import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const VideoShowcase = () => {
  const scrollContainerRef = useRef(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const videoRefs = useRef([]);

  // Fetch videos from backend on mount
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/showcase-videos`);
        setVideos(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching showcase videos:', error);
        // Fallback to default video if API fails
        setVideos([
          {
            id: '1',
            videoId: 't9S3XDcsJaE',
            thumbnail: 'https://img.youtube.com/vi/t9S3XDcsJaE/maxresdefault.jpg',
            engineered: 'Trap Spade'
          }
        ]);
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Auto-play videos when they come into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const iframe = entry.target.querySelector('iframe');
          if (iframe) {
            if (entry.isIntersecting) {
              // Play video when visible
              const src = iframe.src;
              if (!src.includes('autoplay=1')) {
                iframe.src = src.replace('autoplay=0', 'autoplay=1');
              }
            } else {
              // Pause video when not visible
              const src = iframe.src;
              if (src.includes('autoplay=1')) {
                iframe.src = src.replace('autoplay=1', 'autoplay=0');
              }
            }
          }
        });
      },
      { threshold: 0.5 } // Play when 50% visible
    );

    videoRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-black to-purple-950/20 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Studio <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Showcase</span>
          </h2>
          <p className="text-gray-400 text-lg">Watch our latest productions and sessions</p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
          </div>
        ) : (
        <div>
        {/* Scroll Container */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-[40%] -translate-y-1/2 z-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-[40%] -translate-y-1/2 z-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
            aria-label="Scroll right"
          >
            <ChevronRight size={24} />
          </button>

          {/* Horizontal Scroll Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 px-12 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {videos.map((video, index) => (
              <div
                key={video.id}
                ref={(el) => (videoRefs.current[index] = el)}
                className="flex-shrink-0 w-[400px] group snap-start"
              >
                {/* Video Card */}
                <div className="relative bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl overflow-hidden hover:border-purple-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/20">
                  {/* YouTube Embed - Auto-plays on scroll */}
                  <div className="relative aspect-video overflow-hidden">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${video.videoId}?autoplay=0&mute=1&controls=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>

                  {/* Video Info - Only show credits for work we did */}
                  <div className="p-4 space-y-3">
                    <div className="space-y-1 text-sm">
                      {video.engineered && (
                        <div className="flex justify-between text-gray-400">
                          <span className="text-purple-400 font-semibold">Engineered:</span>
                          <span className="text-white font-medium">{video.engineered}</span>
                        </div>
                      )}
                      {video.directed && (
                        <div className="flex justify-between text-gray-400">
                          <span className="text-purple-400 font-semibold">Directed:</span>
                          <span className="text-white font-medium">{video.directed}</span>
                        </div>
                      )}
                      {video.shot && (
                        <div className="flex justify-between text-gray-400">
                          <span className="text-purple-400 font-semibold">Shot by:</span>
                          <span className="text-white font-medium">{video.shot}</span>
                        </div>
                      )}
                      {video.produced && (
                        <div className="flex justify-between text-gray-400">
                          <span className="text-purple-400 font-semibold">Produced:</span>
                          <span className="text-white font-medium">{video.produced}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Instruction Text */}
        <p className="text-center text-gray-500 text-sm mt-6">
          ← Scroll to view more videos →
        </p>
        </div>
        )}
      </div>

      {/* No modal needed - videos play inline */}
    </section>
  );
};

export default VideoShowcase;
